import { type FC, Suspense, type ReactNode } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';
import { triggerConstants } from 'dibs-tracking';

import { useHomePageContext } from '../HomePageContext.tsx';
import { HpSharedFeaturedModuleCarousel } from './HpSharedFeaturedModuleCarousel.tsx';
import {
    HpSharedCarouselTabs,
    type HpCarouselTab,
} from '../HpSharedCarouselTabs/HpSharedCarouselTabs.tsx';
import { ItemTilesCarouselShimmer } from 'dibs-product-tile-carousel/exports/ItemTilesCarouselShimmer.ts';

import { type HpSharedFeaturedModuleContent_viewer$key } from './__generated__/HpSharedFeaturedModuleContent_viewer.graphql.ts';
import { type HpSharedFeaturedModuleContent_user$key } from './__generated__/HpSharedFeaturedModuleContent_user.graphql.ts';

import dibsCss from 'dibs-css';

const CAROUSEL_ITEMS_TO_SHOW_RESP = 5;
const CAROUSEL_ITEMS_TO_SHOW_MOBILE = 1.5;

const viewerFragment = graphql`
    fragment HpSharedFeaturedModuleContent_viewer on Viewer {
        onSaleVerticals {
            category
            displayName
            linkData {
                path
            }
        }
    }
`;

const userFragment = graphql`
    fragment HpSharedFeaturedModuleContent_user on User {
        ...HpSharedFeaturedModuleCarousel_user
    }
`;

const saleModuleTitle = (
    <FormattedMessage id="HpSharedFeaturedModuleContent.title" defaultMessage="Today's Top Deals" />
);

export const HpSharedFeaturedModuleContent: FC<{
    viewer?: HpSharedFeaturedModuleContent_viewer$key | null;
    user?: HpSharedFeaturedModuleContent_user$key | null;
}> = ({ viewer: viewerRef, user: userRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);
    const { isMobile } = useHomePageContext();

    const onSaleVerticals = viewer?.onSaleVerticals || [];

    const tabList: string[] = [];
    const tabMap: Record<string, HpCarouselTab> = {};

    for (const vertical of onSaleVerticals) {
        const { category, displayName, linkData } = vertical || {};
        if (category && displayName && linkData?.path) {
            tabList.push(category);
            tabMap[category] = {
                value: category,
                label: displayName,
                trigger: triggerConstants.TRIGGER_SALE_MODULE,
                viewMoreLink: linkData.path,
            };
        }
    }

    const itemsToShow = isMobile ? CAROUSEL_ITEMS_TO_SHOW_MOBILE : CAROUSEL_ITEMS_TO_SHOW_RESP;

    const renderPanel = (tabName: string): ReactNode => {
        const verticalPath = tabMap[tabName]?.viewMoreLink || null;
        return (
            <Suspense
                fallback={
                    <ItemTilesCarouselShimmer
                        fullWidth={!!isMobile}
                        gapSize="small"
                        itemsToShow={itemsToShow}
                        showArrows={!isMobile}
                        size="medium"
                    />
                }
            >
                <HpSharedFeaturedModuleCarousel
                    user={user}
                    isMobile={isMobile}
                    verticalPath={verticalPath}
                />
            </Suspense>
        );
    };

    if (!tabList.length) {
        return null;
    }

    return (
        <div
            className={classnames(dibsCss.bgWhite, dibsCss.textNoir, {
                [dibsCss.mtLarge]: !isMobile,
            })}
        >
            <HpSharedCarouselTabs
                tabList={tabList}
                tabMap={tabMap}
                title={saleModuleTitle}
                isMobile={isMobile}
                renderPanel={renderPanel}
                renderOnInactive={false}
            />
        </div>
    );
};
